import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDateShort } from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Truck, MapPin, User as UserIcon } from "lucide-react";
import { toast } from "sonner";

const STATUS = [
  { value: "scheduled", label: "Scheduled" },
  { value: "out_for_delivery", label: "Out for delivery" },
  { value: "delivered", label: "Delivered" },
  { value: "failed", label: "Failed" },
];

const StatusPill = ({ status }) => {
  const map = {
    scheduled: "bg-[#E5A93D]/15 text-[#7c5a14] border-[#E5A93D]/40",
    out_for_delivery: "bg-[#D66853]/15 text-[#a44231] border-[#D66853]/40",
    delivered: "bg-primary/10 text-primary border-primary/30",
    failed: "bg-destructive/10 text-destructive border-destructive/30",
  };
  return <span className={`pill capitalize ${map[status]}`}>{status.replace(/_/g, " ")}</span>;
};

export default function Deliveries() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/deliveries");
    setItems(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/deliveries/${id}`, { status });
      toast.success(`Marked ${status.replace("_", " ")}`);
      await load();
    } catch {
      toast.error("Failed to update");
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-[1500px] animate-fade-in">
      <div className="mb-8">
        <div className="label-eyebrow">Deliveries</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">
          Delivery schedule
        </h1>
        <p className="text-muted-foreground mt-1">
          Out-for-delivery, completed, and upcoming runs.
        </p>
      </div>

      {items.length === 0 ? (
        <div className="surface-card p-10 text-center">
          <div className="mx-auto w-full max-w-md">
            <img
              src="https://images.unsplash.com/photo-1665521032636-e8d2f6927053"
              alt="Van"
              className="w-full h-44 object-cover rounded-md mb-6 opacity-90"
            />
            <div className="label-eyebrow">No deliveries</div>
            <h2 className="font-heading text-xl font-semibold mt-2">Nothing on the road today</h2>
            <p className="text-muted-foreground text-sm mt-1">
              Open an order and click &ldquo;Schedule delivery&rdquo; to add one.
            </p>
            <Link
              to="/orders"
              data-testid="go-to-orders"
              className="inline-block mt-4 text-sm text-primary hover:underline"
            >
              Browse orders →
            </Link>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((d) => (
            <div key={d.id} className="surface-card p-5" data-testid={`delivery-card-${d.id}`}>
              <div className="flex items-start justify-between gap-2 mb-3">
                <Link
                  to={`/orders/${d.order_id}`}
                  className="font-mono text-sm text-primary hover:underline"
                >
                  {d.order_number}
                </Link>
                <StatusPill status={d.status} />
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <Truck className="w-4 h-4 text-muted-foreground" />
                  <span className="font-medium">{fmtDateShort(d.scheduled_for)}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <UserIcon className="w-4 h-4" />
                  <span>{d.customer}</span>
                </div>
                {d.address && (
                  <div className="flex items-start gap-2 text-muted-foreground">
                    <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>{d.address}</span>
                  </div>
                )}
                {d.driver && (
                  <div className="text-xs text-muted-foreground">Driver: {d.driver}</div>
                )}
              </div>
              <div className="mt-4">
                <Select value={d.status} onValueChange={(v) => setStatus(d.id, v)}>
                  <SelectTrigger className="h-8 text-xs" data-testid={`delivery-status-${d.id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ))}
        </div>
      )}
      {loading && items.length === 0 && (
        <div className="text-sm text-muted-foreground p-6">Loading…</div>
      )}
    </div>
  );
}
